import { httpPost } from './http'
import { splitDelimitedString } from '~/cynqueModules/componentHelpers/entityMaskForm/delimitedList'
import * as errors from './errors'
import type { TEntityId } from '~/types/models'
import type { ICaseEmail, ICaseReplyForPost } from '~/types/case/models'
import {
  pickedDocumentIds,
  pickedFiles,
} from '~/cynqueModules/caseReplyAttachments'

const apiEntityPath = '/api/cases'

/** The title the API gives a suppressed-recipient 409. */
const SUPPRESSED_TITLE = 'Recipient address is suppressed'

/**
 * Pull the suppression fields out of a problem-details body.
 *
 * ⚠ Reads BOTH shapes on purpose. RFC 9457 extension members are top-level, and
 * that is what litestar's ProblemDetailsException is expected to emit for its
 * `extra` mapping -- but litestar is not installed here, so that could not be
 * confirmed from source, and a nested `extra` object is tolerated rather than
 * guessed at. Whoever first runs this against the real API should check which
 * arrives and can then delete the other branch.
 */
function suppressionFieldsFrom(body: unknown) {
  const b = (body ?? {}) as Record<string, unknown>
  const nested = (b.extra ?? {}) as Record<string, unknown>
  const pick = (key: string) => nested[key] ?? b[key]
  return {
    recipient: pick('recipient') as string | undefined,
    event: pick('event') as string | undefined,
    bounceType: pick('bounceType') as string | null | undefined,
    canReactivate: pick('canReactivate') as boolean | undefined,
    occurredAt: pick('occurredAt') as string | null | undefined,
  }
}

/** The title the API gives the 409 for a reply on a MERGED case (#1658). */
const CASE_MERGED_TITLE = 'Case was merged'

/**
 * The survivor's id from the merged-case 409's detail, which reads
 * "Case=<id> was merged into Case=<survivor>; reply on that case." Null when
 * the wording changes -- the error still says what happened, without the id.
 */
export function mergedIntoIdFrom(body: unknown): string | null {
  const detail = (body as Record<string, unknown> | null)?.detail
  if (typeof detail !== 'string') {
    return null
  }
  return detail.match(/merged into Case=([^;\s]+)/)?.[1] ?? null
}

/**
 * The titles of the 400s that refuse a reply's ATTACHMENTS rather than its
 * text (cynque-api-server #1674) -- worded for the user and naming the files,
 * unlike a body-validation 400.
 */
const ATTACHMENT_REFUSAL_TITLES = [
  'Empty attachment',
  'Attachment type not allowed',
]

/** What a too-large reply is told when the 413 carries no detail of its own. */
const REPLY_TOO_LARGE_FALLBACK =
  'These attachments are too large to send by email. Send fewer files, or' +
  ' share them another way.'

function isSuppressionConflict(body: unknown): boolean {
  const b = (body ?? {}) as Record<string, unknown>
  // Matched on the title rather than on the presence of `recipient`, so a future
  // 409 that happens to carry a recipient is not silently read as this one.
  return b.title === SUPPRESSED_TITLE
}

/**
 * A suppressed-recipient 409 as itself, or undefined for any other error.
 *
 * httpPost / httpPatchMerge type EVERY 409 as UpdateConflictError -- correct for
 * a version conflict, wrong for this one, whose user-facing message would
 * otherwise be "changed by someone else". Shared with services/emails.ts: an
 * activity email's send refuses a suppressed recipient the same way
 * (cynque-api-server #1770).
 */
export function suppressedRecipientErrorFrom(
  error: unknown,
  apiPath: string
): errors.SuppressedRecipientError | undefined {
  if (
    !(error instanceof errors.UpdateConflictError) ||
    !isSuppressionConflict(error.body)
  ) {
    return undefined
  }
  const body = error.body as Record<string, unknown>
  return new errors.SuppressedRecipientError(
    apiPath,
    (body.detail as string) ?? SUPPRESSED_TITLE,
    suppressionFieldsFrom(error.body),
    error.body
  )
}

/**
 * The request body: `multipart/form-data`, ALWAYS (cynque-api-server #1674).
 *
 * One `payload` part carrying the reply as JSON, and one `files` part per new
 * attachment, under that repeated name, in the order picked -- the server
 * attaches them in that order, after any `documentIds`.
 *
 * ⚠ Built here, not by buildPostBody. That goes multipart only when a key holds
 * a single File, so a reply with no files would go as JSON (a 415) and an array
 * of files would be stringified into `payload` (a 400 for the unknown key).
 */
export function buildCaseReplyFormData(
  reply: ICaseReplyForPost,
  files: File[] = []
): FormData {
  const formData = new FormData()
  formData.append('payload', JSON.stringify(reply))
  files.forEach((file) => formData.append('files', file, file.name))
  return formData
}

/**
 * Send an email from a case (the Case page's Send Email).
 *
 * An action, not a CRUD create, which is why it has its own endpoint rather than
 * a flag on POST /api/casenotes. The user sets the recipients and subject; the
 * server adds the RFC 5322 threading headers that make it land in the
 * customer's existing conversation.
 *
 * The contract is cynque-api-server #1666 -- see ICaseReplyForPost. ⚠ Its
 * model forbids unknown keys, so the payload must carry exactly the declared
 * fields: anything extra is a 400. Since #1674 it travels as the `payload` part
 * of a multipart body, beside the new files (buildCaseReplyFormData); the
 * server stores each file as a case document in the reply's own transaction,
 * so a refused reply leaves no file behind.
 *
 * ⚠ **A 201 means recorded, not delivered.** The request writes a `queued`
 * message and returns it; a worker does the sending. Delivery is reported
 * later through ICaseEmail.deliveryState, which the UI must show: there is no
 * notification mechanism behind it, so a case that hides a `bounced` leaves the
 * agent believing they replied.
 *
 * The recipient no longer has to be the correspondent who wrote in: the form
 * prefills To with them (caseReplyRecipient mirrors the server's
 * `inbound.reply_to ?? inbound.from_address`), and the user can change it.
 *
 * The failures the caller has to tell apart:
 *  - **400** -- a payload that fails validation (an address that is not bare,
 *    one repeated across to/cc/bcc, over 50 recipients, a blank or multi-line
 *    subject, an unknown key), an unknown/foreign `documentIds` entry, or no
 *    `to` on a case nobody has emailed. The form checks the same rules first.
 *  - **400 / 413 on the attachments** -- an empty file, a type the mail
 *    provider blocks (new files and `documentIds` alike), or more than its
 *    10 MiB once encoded. Thrown as ReplyAttachmentsRefusedError, carrying the
 *    server's detail, which names the files. The form checks these first too.
 *  - **409** -- a recipient's address is SUPPRESSED after an earlier bounce or
 *    spam complaint. Thrown as SuppressedRecipientError, NOT the bare
 *    UpdateConflictError httpPost would otherwise raise, whose message blames a
 *    stale version this request never sent. Recoverable: nothing has been written
 *    yet, and `sendToSuppressed` proceeds knowingly.
 *  - **500** -- replies are not configured on this server (DOMAIN_NAME unset).
 *  - **415** -- the body was not multipart. Left as APIUnhandledStatus: it can
 *    only mean this client and the server disagree about the contract.
 *
 * Returns the queued message, as the overview's `caseEmails` carries it
 * (cynque-api-server #1737; it used to return a CaseNote).
 */
export async function postCaseReply(
  caseId: TEntityId,
  reply: ICaseReplyForPost,
  files: File[] = []
) {
  const callingMn = postCaseReply.name
  const apiPath = `${apiEntityPath}/${caseId}/replies`
  try {
    return (await httpPost<ICaseReplyForPost>({
      callingMn,
      apiPath,
      entity: reply,
      multipartBody: buildCaseReplyFormData(reply, files),
    })) as unknown as ICaseEmail
  } catch (error: unknown) {
    // The attachments were refused, in words written for the user. httpPost
    // types these as a ValidationError (whose detail the dialog hides) or a
    // PayloadTooLargeError (worded around the document-upload cap).
    if (
      (error instanceof errors.ValidationError &&
        ATTACHMENT_REFUSAL_TITLES.includes(
          (error.body as Record<string, unknown> | null)?.title as string
        )) ||
      error instanceof errors.PayloadTooLargeError
    ) {
      const body = (error.body ?? {}) as Record<string, unknown>
      const extra = (body.extra ?? {}) as Record<string, unknown>
      const filenames = (extra.filenames ?? body.filenames) as
        | string[]
        | undefined
      throw new errors.ReplyAttachmentsRefusedError(
        error.statusCode ?? 400,
        apiPath,
        typeof body.detail === 'string' && body.detail.trim()
          ? body.detail.trim()
          : error instanceof errors.PayloadTooLargeError
          ? REPLY_TOO_LARGE_FALLBACK
          : String(body.title),
        Array.isArray(filenames) ? filenames : [],
        error.body
      )
    }
    // httpPost types EVERY 409 as UpdateConflictError -- correct for a create
    // that collides with an existing row, wrong for this one. Re-throw the
    // suppression case as itself; leave anything else exactly as it was.
    const suppressed = suppressedRecipientErrorFrom(error, apiPath)
    if (suppressed) {
      throw suppressed
    }
    // The case was merged while this dialog was open (Send Email is disabled on
    // a merged case, so this is the race): not a version conflict.
    if (
      error instanceof errors.UpdateConflictError &&
      (error.body as Record<string, unknown> | null)?.title ===
        CASE_MERGED_TITLE
    ) {
      throw new errors.CaseMergedError(
        apiPath,
        mergedIntoIdFrom(error.body),
        error.body
      )
    }
    throw error
  }
}

/**
 * The `caseReply` arm of services/crud/createEntity.ts.
 *
 * The standard dialog machinery hands over one flat entity built from the
 * caseReply input spec; this turns it into the action the endpoint actually is.
 * `case` is an unrendered field stamped from the dialog's parent object, and its
 * id is what makes the request path -- there is no /api/casereplies collection.
 *
 * ⚠ `case` is stripped from the body: the path carries the case (and an extra
 * key is a 400). To / cc / bcc arrive as the form's comma-joined strings
 * (delimitedStringSeparator) and are split back into the address lists the
 * endpoint takes.
 */
export async function createCaseReply(entity: unknown) {
  const fn = `${createCaseReply.name} |`
  const {
    case: caseRef,
    note,
    subject,
    to,
    cc,
    bcc,
    documents,
    files,
    sendToSuppressed,
    inReplyToEmailId,
  } = (entity ?? {}) as {
    case?: { id?: TEntityId }
    note?: string
    subject?: string
    to?: string
    cc?: string
    bcc?: string
    documents?: unknown
    files?: unknown
    sendToSuppressed?: boolean
    inReplyToEmailId?: TEntityId
  }
  if (!caseRef?.id) {
    // Thrown rather than defaulted: without the case there is no path, and a
    // guessed one would post a reply onto somebody else's case.
    throw new Error(
      `${fn} no case on the reply payload -- the caseReply spec's unrendered` +
        ' `case` field is stamped from the dialog parent; check that the card' +
        " passes a parent of { entityName: 'case', object: <case> }."
    )
  }
  const toList = splitDelimitedString(to)
  const ccList = splitDelimitedString(cc)
  const bccList = splitDelimitedString(bcc)
  // Documents already on the case go by id; files from the computer go as
  // multipart parts, and the server stores each as a new case document.
  const documentIds = pickedDocumentIds(documents)
  const created = await postCaseReply(
    caseRef.id,
    {
      note: note ?? '',
      ...(subject ? { subject } : {}),
      ...(toList.length ? { to: toList } : {}),
      ...(ccList.length ? { cc: ccList } : {}),
      ...(bccList.length ? { bcc: bccList } : {}),
      ...(documentIds.length ? { documentIds } : {}),
      ...(sendToSuppressed ? { sendToSuppressed: true } : {}),
      // Only when a row's Reply named it -- see the caseReply spec's field.
      ...(inReplyToEmailId ? { inReplyToEmailId } : {}),
    },
    pickedFiles(files)
  )
  /**
   * Fold the server's message back onto the entity the dialog is holding.
   *
   * saveEntityData hands its after-request callback the object it SENT
   * (copying only hrid and version from the response), and what the caseReply
   * form sends is its dirty fields (`note`, `subject`, `to` / `cc` / `bcc`,
   * `documents`, `files`, `case`, `sendToSuppressed`, `inReplyToEmailId`) --
   * not an email. Mutating
   * that same object makes it the whole CaseEmail, which the page falls back to
   * appending when its refetch fails (caseReplyCreated). The form-only fields
   * are left on it and ignored; nothing downstream reads them.
   */
  if (created && typeof created === 'object') {
    Object.assign(entity as Record<string, unknown>, created)
  }
  return created
}
